/**
 * WP-009 / ISSUE-216 — Facilitator actions against Krysalis API (not Local Bridge):
 * close session + IntentML snapshot (ISSUE-219), company IntentML import (ISSUE-215).
 */

import React from 'react';
import { Alert, Button, Card, Space, Typography } from 'antd';
import {
  DownloadOutlined,
  ImportOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { isLocalBridgeApiEnabled } from '../../services/bridgeSessionDataAdapter';
import { useBridgeSessionIntentmlFacilitator } from './useBridgeSessionIntentmlFacilitator';
import styles from './BridgeSessionIntentmlFacilitatorPanel.module.css';

const { Text } = Typography;

interface BridgeSessionIntentmlFacilitatorPanelProps {
  sessionId: string;
  companyId?: string | null;
}

export const BridgeSessionIntentmlFacilitatorPanel: React.FC<BridgeSessionIntentmlFacilitatorPanelProps> = ({
  sessionId,
  companyId,
}) => {
  const {
    fileInputRef,
    importErrors,
    importLoading,
    completeLoading,
    downloadLoading,
    lastIntentmlUrl,
    onCompleteSession,
    onDownloadSnapshot,
    onPickImportFile,
    onImportFileChange,
  } = useBridgeSessionIntentmlFacilitator(sessionId, companyId);

  if (!sessionId || isLocalBridgeApiEnabled()) {
    return null;
  }

  return (
    <Card size="small" title="Platform (IntentML)" className={styles.panel}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text type="secondary" className={styles.sectionTitle}>
            Session snapshot (ISSUE-219)
          </Text>
          <div className={styles.actions}>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={completeLoading}
              onClick={onCompleteSession}
            >
              Complete session
            </Button>
            <Button icon={<DownloadOutlined />} loading={downloadLoading} onClick={onDownloadSnapshot}>
              Download IntentML JSON
            </Button>
          </div>
          {lastIntentmlUrl && (
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
              API URL: {lastIntentmlUrl}
            </Text>
          )}
          <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            Download returns 404 until the session is closed. If you already completed this session earlier, download
            should still work.
          </Text>
        </div>

        <div>
          <Text type="secondary" className={styles.sectionTitle}>
            Import to company (ISSUE-215)
          </Text>
          <div className={styles.actions}>
            <Button icon={<ImportOutlined />} loading={importLoading} onClick={onPickImportFile}>
              Import IntentML JSON…
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              className={styles.hiddenInput}
              onChange={onImportFileChange}
            />
          </div>
          {importErrors && importErrors.length > 0 && (
            <Alert
              type="error"
              showIcon
              message="Import rejected"
              description={
                <ul className={styles.errorList}>
                  {importErrors.map((er, i) => (
                    <li key={`${er.code}-${er.path}-${i}`}>
                      <strong>{er.code}</strong> {er.path}: {er.message}
                    </li>
                  ))}
                </ul>
              }
              style={{ marginTop: 8 }}
            />
          )}
        </div>
      </Space>
    </Card>
  );
};
